// /team/:userId - member profile (lite). Identity comes from
// /api/firm/staff; the assigned-case list from the admin-gated
// /api/firm/assignments/:userId - other roles see the person without the
// caseload. The old role tabs (lead/paralegal/reviewer) described a shape
// the server does not have: a Case carries one assignedTo. The richer
// page (activity feed, permissions) waits on the access-model milestone.

import { useNavigate, useParams } from "react-router-dom";
import { useFirmStaff } from "../../hooks/useFirmStaff";
import { useMemberAssignments } from "../../hooks/useMemberAssignments";
import type { AssignedCase } from "../../api/firmWorkload";
import { colorFor } from "../../lib/staffVisuals";
import { caseTypeLabel, stageLabel, statusLabel } from "../../lib/caseLabels";
import { initialsOf } from "../../api/auth";
import { recordStep, usePageTrail } from "../chrome/trail";
import { EntityLink } from "../chrome/EntityLink";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { MAIL, SEP } from "../chrome/SepList";

function clientNameOf(c: AssignedCase): string {
  const p = c.clientData?.personal;
  return [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "(unknown client)";
}

function CaseRow({ c }: { c: AssignedCase }) {
  const navigate = useNavigate();
  const openDeadlines = (c.deadlines ?? []).filter((d) => !d.completed).length;
  const target = c.eId ? `/matter/${c.eId}` : null;
  return (
    // Whole-row click via div onClick + one real link inside (the eId chip)
    // - the C6 ledger-row contract, no anchor nests inside another.
    <div
      className="mrow cc-row-lift"
      style={target ? { cursor: "pointer" } : undefined}
      onClick={target ? () => navigate(target) : undefined}
    >
      <div className="who">
        {clientNameOf(c)}
        <div className="meta">
          {[
            caseTypeLabel(c.caseType),
            stageLabel(c.caseStage),
            c.status !== "active" ? statusLabel(c.status) : null,
          ]
            .filter(Boolean)
            .join(SEP)}
        </div>
      </div>
      <span className="meta">
        {openDeadlines} open deadline{openDeadlines === 1 ? "" : "s"}
      </span>
      {c.eId ? (
        <EntityLink kind="matter" id={c.eId} eID={c.eId} stopPropagation subtle className="eid">
          {c.eId}
        </EntityLink>
      ) : (
        <span className="eid">{c.caseNumber ?? ""}</span>
      )}
    </div>
  );
}

export function MemberDetail() {
  const { userId } = useParams<{ userId: string }>();
  const staffQ = useFirmStaff();
  const casesQ = useMemberAssignments(userId);
  const member = staffQ.data?.find((s) => s.id === userId);
  usePageTrail([
    { label: "Team", to: "/team" },
    recordStep(member?.fullName, staffQ.isLoading, "Member"),
  ]);

  return (
    <main className="cc-main">
      <div className="cc-body">
        {staffQ.isLoading && (
          <SkeletonGroup label="Loading member">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={120} radius={10} />
          </SkeletonGroup>
        )}
        {staffQ.isError && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
            Could not reach the server - the member profile is unavailable.
          </div>
        )}
        {staffQ.data && !member && (
          <div className="cc-card" style={{ padding: 20 }}>
            <p style={{ margin: 0, color: "var(--muted)" }}>
              No team member found for <code>{userId ?? "(empty)"}</code>.
            </p>
          </div>
        )}

        {member && (
          <div className="cc-card" style={{ marginBottom: 14 }}>
            <div className="member-hero">
              <div className="av" style={{ background: colorFor(member.id) }}>
                {initialsOf(member.fullName)}
              </div>
              <div style={{ minWidth: 0 }}>
                <h2>{member.fullName}</h2>
                <div className="role">
                  {member.role}
                  {member.isPlaceholder ? " - not signed in yet" : ""}
                </div>
                <div className="contact">
                  {member.email && (
                    <a href={`mailto:${member.email}`}>
                      {MAIL} {member.email}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="member-section">
              <h3>
                Assigned matters
                {casesQ.data?.allowed && (
                  <span style={{ color: "var(--muted)", fontWeight: 500 }}>
                    {SEP}
                    {casesQ.data.cases.length}
                  </span>
                )}
              </h3>
              {casesQ.isLoading && <Skeleton width="100%" height={48} radius={8} />}
              {casesQ.isError && (
                <div style={{ fontSize: 12, color: "var(--warn-600)" }}>
                  Could not reach the server - assignments are unavailable.
                </div>
              )}
              {casesQ.data && !casesQ.data.allowed && (
                <div style={{ fontSize: 12, color: "var(--muted)" }}>
                  The assigned-case list requires an admin sign-in.
                </div>
              )}
              {casesQ.data?.allowed && casesQ.data.cases.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--muted)" }}>No matters assigned yet.</div>
              )}
              {casesQ.data?.allowed && casesQ.data.cases.length > 0 && (
                <div className="member-matters">
                  {casesQ.data.cases.map((c) => (
                    <CaseRow key={c._id} c={c} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
